import {useQuery} from '@tanstack/react-query'
import type {AxiosResponse} from 'axios'
import isEqual from 'lodash.isequal'

import {EndpointsConfigTypes} from 'interfaces'
import {SettingsService} from 'services/api'
import {queryClient} from 'services/queryClient'
import {useEndpointConfigSnapshot} from 'hooks/snapshots/useEndpointConfigSnapshot'

export const ENDPOINTS_SETTINGS_QUERY_KEY = 'getEndpointsConfig'

export const defaultEndpointSettingsQueryResponse: EndpointsConfigTypes = {
  allowedDomains: [],
  antiTamperPassword: '',
  authProvider: '',
  endpointVersion: '',
  failClosed: false,
  installedEndpoints: 0,
  requireDelegatedAuth: false,
}

type EndpointSettingsQueryCache = Partial<AxiosResponse<EndpointsConfigTypes>>

export const useEndpointSettingsQuery = (
  isEnabled = true,
): {
  endpointsConfigData: EndpointsConfigTypes
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
} => {
  const {endpointConfigSnapshot, setEndpointConfigSnapshot} =
    useEndpointConfigSnapshot()

  const {
    data: endpointsConfigData,
    isLoading,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: [ENDPOINTS_SETTINGS_QUERY_KEY],
    queryFn: () => SettingsService.getEndpointsConfig(),
    select: endpointsConfigResponse => endpointsConfigResponse.data,
    onSuccess: data => {
      if (!data?.endpointVersion) {
        return
      }
      if (!endpointConfigSnapshot || !isEqual(endpointConfigSnapshot, data)) {
        setEndpointConfigSnapshot(data)
      }
    },
    enabled: isEnabled,
  })

  return {
    endpointsConfigData:
      endpointsConfigData ?? defaultEndpointSettingsQueryResponse,
    isLoading,
    isSuccess,
    isError,
  }
}

export const getEndpointSettingsQueryCache = (): EndpointSettingsQueryCache => {
  const queryData = queryClient.getQueryData<EndpointSettingsQueryCache>([
    ENDPOINTS_SETTINGS_QUERY_KEY,
  ])
  return queryData ?? {}
}

export const setEndpointsSettingCache = (
  payload: EndpointsConfigTypes,
): void => {
  const endpointsSettingConfig = getEndpointSettingsQueryCache()
  const updatedCacheData = {
    ...endpointsSettingConfig,
    data: payload,
  }
  queryClient.setQueryData([ENDPOINTS_SETTINGS_QUERY_KEY], updatedCacheData)
}
